import { useEffect, useState, useRef, useCallback } from "react";
import { ClipboardList, RefreshCw, Calculator } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/sonner";

// FileMaker ↔ App two-way procurement quotes sync (admin only) — pulls
// records from FM, then pushes WebApp-side creates/updates/deletes back.
export function ProcurementSyncCard() {
  const { t } = useLanguage();
  const [status, setStatus] = useState(null);
  const [starting, setStarting] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [since, setSince] = useState("");   // 2026-06 · 同步起始日期（存数据库）
  const timer = useRef(null);

  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/procurement-quotes/sync/status");
      setStatus(data);
      setSince((prev) => prev || data.since || "");
      if (data.state !== "running" && timer.current) {
        clearInterval(timer.current);
        timer.current = null;
      }
    } catch (error) {
      // not admin or backend down — hide silently but log for devs
      if (error?.response?.status !== 403 && error?.response?.status !== 401) {
        console.warn("Procurement-quotes sync status fetch failed:", error);
      }
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    // 2026-06 · Keep the realtime line fresh (last incremental pull time).
    const slow = setInterval(fetchStatus, 30000);
    return () => { clearInterval(slow); if (timer.current) clearInterval(timer.current); };
  }, [fetchStatus]);

  useEffect(() => {
    if (status?.state === "running" && !timer.current) {
      timer.current = setInterval(fetchStatus, 3000);
    }
  }, [status, fetchStatus]);

  const startSync = async () => {
    setStarting(true);
    try {
      await api.post("/procurement-quotes/sync", since ? { since } : {});
      toast.success(t("fm.started"));
      fetchStatus();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setStarting(false);
    }
  };

  const backfillQtyHq = async () => {
    setBackfilling(true);
    try {
      const { data } = await api.post("/procurement-quotes/admin/backfill-qty-hq");
      toast.success(t("pq.backfillQtyHqDone").replace("{n}", String(data.fixed ?? 0)));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBackfilling(false);
    }
  };

  const running = status?.state === "running";
  const pct = status?.total ? Math.round((status.processed / status.total) * 100) : 0;

  return (
    <div className="bg-card border border-border rounded-md p-6 space-y-4 mb-5" data-testid="pq-sync-card">
      <h3 className="font-heading text-base font-bold tracking-tight flex items-center gap-2">
        <ClipboardList size={16} className="text-brand" /> {t("pq.syncTitle")}
      </h3>
      <p className="text-xs text-muted-foreground -mt-2">{t("pq.syncHint")}</p>
      <p className="text-[11px] font-mono text-muted-foreground -mt-1" data-testid="pq-realtime-hint">
        {t("pq.realtimeHint").replace("{n}", status?.realtime_minutes ?? 15)}
        {status?.recent_field ? ` · ${t("pq.realtimeField")}: ${status.recent_field}` : ""}
        {status?.last_recent_at ? ` · ${t("pq.realtimeLast")} ${new Date(status.last_recent_at).toLocaleTimeString()}` : ""}
      </p>

      {/* 2026-06 · 同步起始日期存数据库 → 生产环境可直接改，不用重新部署。
          结束日期自动到今天（用户选择）。实时增量拉取不受此设置影响。 */}
      <p className="text-xs text-muted-foreground" data-testid="pq-sync-counts">
        {t("pq.syncCounts")
          .replace("{p}", ["pending", "pushing", "retry"].reduce((n, key) => n + (status?.sync_counts?.[key] || 0), 0))
          .replace("{a}", ["conflict", "missing", "uncertain", "review"].reduce((n, key) => n + (status?.sync_counts?.[key] || 0), 0))}
      </p>
      <div className="space-y-1 max-w-[220px]">
        <Label className="text-[11px] text-muted-foreground">{t("pq.sinceLabel")}</Label>
        <Input value={since} onChange={(e) => setSince(e.target.value)} placeholder="01/01/2025"
          className="h-8 font-mono" data-testid="pq-since-input" />
        <p className="text-[10px] text-muted-foreground">{t("pq.sinceHint")}</p>
      </div>

      {status && status.state !== "idle" && (
        <div className="space-y-2" data-testid="pq-sync-status">
          {running && (
            <div className="w-full h-2 bg-secondary rounded-full overflow-hidden">
              <div className="h-full bg-brand transition-all duration-500" style={{ width: `${pct}%` }} />
            </div>
          )}
          <p className="text-xs font-mono text-muted-foreground">
            {running ? `${t("pq.syncing")} ${status.processed}/${status.total} (${pct}%)` :
              status.state === "done" ? (
                t("pq.syncDone").replace("{n}", status.upserted)
                + " "
                + t("pq.syncPushSummary")
                  .replace("{c}", status.pushed_creates || 0)
                  .replace("{u}", status.pushed_updates || 0)
                  .replace("{d}", status.pushed_deletes || 0)
              ) :
              `${t("fm.failed")}: ${status.error || ""}`}
          </p>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Button onClick={startSync} disabled={running || starting} data-testid="pq-sync-button"
          variant="outline" className="rounded-md">
          <RefreshCw size={14} className={running ? "animate-spin" : ""} />
          {running ? t("pq.syncing") : t("pq.sync")}
        </Button>
        {/* 2026-06 · One-off repair: fill QTY/HQ from carton data for rows FM
            imported empty (new pulls compute it automatically). */}
        <Button onClick={backfillQtyHq} disabled={backfilling} data-testid="pq-backfill-qtyhq-button"
          variant="outline" className="rounded-md">
          <Calculator size={14} /> {backfilling ? "…" : t("pq.backfillQtyHq")}
        </Button>
      </div>
    </div>
  );
}
